import React, { useState, useRef, useEffect } from 'react';
import _merge from 'lodash/merge';
import { SPACING_8, SPACING_4, SPACING_2 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/spacing';
import { FONT_SIZE_12, FONT_SIZE_14 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/fontSize';
import useFocusManager from '@restart/hooks/useFocusManager';
import { getContext, getTextWidth } from 'common';

import TextInput, { TextInputProps } from 'components/TextInput';
import SearchwChipsList, { SearchwChipsListProps } from './SearchwChipsList';

const EMPTY_STRING = '';
const MAX_FIELD_WIDTH = '0.3125rem';

const FIELD_FONT_STYLE = {
  'font-size': '12px',
  'font-family': 'NokiaPureRegular, Arial, sans-serif'
};

const getStyle = ({ lastInput, empty, subField }) => ({
  flex: lastInput ? '1' : '0 1 auto',
  width: 'auto',
  minWidth: empty ? SPACING_8 : 'initial',
  padding: `0 ${SPACING_2}`,
  maxWidth: (empty && !lastInput && !subField) ? MAX_FIELD_WIDTH : 'none',
  minHeight: '100%'
});

const getSubFieldStyle = (subField, theme) => subField ? {
  background: theme.background,
  color: theme.content
} : {};

interface SearchwChipsTextInputListProps extends Omit<SearchwChipsListProps, 'contextMenu'> {
  contextMenu?: {
    event?: {},
    items: {
      key: string,
      content?: React.ReactNode,
      onClick?: React.MouseEventHandler
    }[]
  },
  autocomplete?: {
    event?: {},
    items: {
      label: string,
      onClick?: React.MouseEventHandler
    }[]
  };
}
export interface SearchwChipsTextInputProps extends Omit<TextInputProps, 'value'> {
  /** The value of the field. */
  value?: string;
  /** Set `true`, if the field is the last one in its container. */
  lastInput?: boolean;
  /** The tab index. */
  tabIndex?: number;
  /* To configure the attached list, refer to the <SearchwChipsList> documentation for more information. */
  listProps?: SearchwChipsTextInputListProps;
  /** Set to true, if list is focused. */
  listFocused?: boolean;
  /** Set `true` to render subField variant (used in Scope/Condition & Query supported SearchwChips variant). */
  subField?: boolean;
  /** Applied only for subField variant, set `true` when subfield or any adjacent subfield is focused. */
  active?: boolean;
  /** Set `true` when field is in focus state. */
  focused?: boolean;
  /** The size of the parent SearchwChips. */
  size: 'small' | 'medium';
  /** Set `true` if the focus is on the popup, such as calendar. */
  popupFocused?: boolean;
  /**  Attributes applied to the input element. Note: DOM attributes should be camelCased. */
  inputProps?: React.ComponentPropsWithRef<'input'>,
  /** To render any node attached to the field or sub-field. */
  renderPopup?(any): JSX.Element;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

/**
 * Any other props than the `SearchwChipsTextInput` props will be provided to the <TextInput/>.
 * This component is used to render the simple fields between chips or when the chip is in edit mode.
 * */
const SearchwChipsTextInput = React.forwardRef<HTMLElement, SearchwChipsTextInputProps>((props: SearchwChipsTextInputProps, ref) => getContext(({ theme }) => {
  const {
    value,
    lastInput,
    inputProps,
    size,
    tabIndex,
    listProps,
    listFocused,
    popupFocused,
    subField,
    active,
    focused: focusedProp,
    theme: themeProps,
    renderPopup,
    ...otherProps
  } = props;
  const {
    contextMenu,
    autocomplete,
    triggerEl,
    searchWChipsListRef,
    ...otherListProps
  } = listProps;
  const autocompleteListRef = useRef(null);
  const textInputRef = useRef(null);
  const [focused, setFocusState] = useState(false);
  const {
    onBlur: onContainerBlur,
    onFocus: onContainerFocus
  } = useFocusManager({
    isDisabled: () => false,
    onChange: nextFocused => setFocusState(nextFocused)
  });
  const resolvedTheme = _merge({}, theme.SEARCH_W_CHIPS.subField, themeProps);
  const stateAwareTheme = (focused || listFocused || popupFocused)
    ? resolvedTheme.focused
    : active
      ? resolvedTheme.active
      : resolvedTheme.normal;

  const empty = value === EMPTY_STRING;

  useEffect(() => {
    setFocusState(focusedProp);
  }, [focusedProp]);

  let inputFontStyle;
  if (textInputRef.current) {
    const targetChild = textInputRef.current.children[0]
      && textInputRef.current.children[0].children[0]
      && textInputRef.current.children[0].children[0].children[0];
    const inputStyle = targetChild ? window.getComputedStyle(targetChild) : FIELD_FONT_STYLE;
    inputFontStyle = ` ${(inputStyle['font-size'] as string)} ${(inputStyle['font-family'] as string)}`;
  }

  return (
    <span
      ref={ref}
      style={{ ...getStyle({ lastInput, empty, subField }) }}
      onFocus={onContainerFocus}
      onBlur={onContainerBlur}
    >
      <TextInput
        ref={textInputRef}
        style={getStyle({ lastInput, empty, subField })}
        inputProps={{
          style: {
            border: 'none',
            boxShadow: 'none',
            maxWidth: empty && !lastInput ? MAX_FIELD_WIDTH : 'initial',
            width: lastInput ? '100%' : empty ? 'initial' : `${getTextWidth(value, inputFontStyle)}rem`,
            margin: `${SPACING_4} 0`,
            height: size === 'small' ? '1.25rem' : '2rem',
            minHeight: size === 'small' ? '1.25rem' : '2rem',
            fontSize: size === 'small' ? FONT_SIZE_12 : FONT_SIZE_14,
            padding: 0,
            ...getSubFieldStyle(subField, stateAwareTheme)
          },
          ...inputProps
        }}
        value={value}
        {...otherProps}
      />
      <SearchwChipsList
        {...{
          list: contextMenu,
          triggerEl,
          searchWChipsListRef
        }}
      />
      {((focused || listFocused) && !contextMenu) && (
        <SearchwChipsList
          ref={autocompleteListRef}
          {...{
            list: autocomplete,
            triggerEl,
            searchWChipsListRef,
            ...listProps
          }}
          {...otherListProps}
        />)
      }
      {(renderPopup && !contextMenu) &&
        renderPopup({ positioningProps: { triggerEl: textInputRef } })
      }
    </span>
  );
}));

SearchwChipsTextInput.defaultProps = {
  value: EMPTY_STRING,
  lastInput: false,
  inputProps: {},
  listProps: {},
  listFocused: false,
  tabIndex: undefined,
  subField: false,
  active: false,
  focused: false,
  theme: {},
  popupFocused: false,
  renderPopup: undefined
};

SearchwChipsTextInput.displayName = 'SearchwChipsTextInput';
export default SearchwChipsTextInput;
